// Subscription server functions — Phase 1.
// - getMySubscription: trial + plan + status for the signed-in user
// - listActivePlans: public list of plans visible on /portal/subscription
// - createSubscriptionOrder: insert pending subscription_orders row, return ref
// - listMySubscriptionOrders: order history for the customer dashboard
// - admin: list/save/toggle plans, list everyone's subscriptions
import { createServerFn } from "@tanstack/react-start";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import { z } from "zod";
import { assertAdmin } from "@/lib/api/_admin-guard";

function makeReference(userId: string) {
  const d = new Date();
  const yyyymmdd =
    d.getUTCFullYear().toString() +
    String(d.getUTCMonth() + 1).padStart(2, "0") +
    String(d.getUTCDate()).padStart(2, "0");
  const userShort = userId.replace(/-/g, "").slice(0, 6).toUpperCase();
  const tail = Math.random().toString(36).slice(2, 8).toUpperCase();
  return `NDLCS-${yyyymmdd}-U${userShort}-S${tail}`;
}

export const getMySubscription = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const sb = context.supabase;
    const [{ data: profile }, { data: sub }, { data: hasAccess }] =
      await Promise.all([
        sb
          .from("profiles")
          .select("trial_started_at, trial_ends_at, trial_consumed, full_name, email")
          .eq("id", context.userId)
          .maybeSingle(),
        sb
          .from("user_subscriptions" as any)
          .select(
            "plan_code, status, current_period_start, current_period_end, next_billing_at, cancelled_at, last_order_reference",
          )
          .eq("user_id", context.userId)
          .maybeSingle(),
        sb.rpc("has_active_access", { _user_id: context.userId }),
      ]);

    const trialEndsAt = profile?.trial_ends_at
      ? new Date(profile.trial_ends_at as any)
      : null;
    const now = new Date();
    const trialActive = !!(trialEndsAt && trialEndsAt > now);
    const subStatus = (sub as any)?.status ?? "inactive";
    const subActive =
      subStatus === "active" &&
      (sub as any)?.current_period_end &&
      new Date((sub as any).current_period_end) > now;

    return {
      profile: profile ?? null,
      subscription: sub ?? null,
      trial: {
        startedAt: profile?.trial_started_at ?? null,
        endsAt: profile?.trial_ends_at ?? null,
        active: trialActive,
        msRemaining: trialEndsAt ? Math.max(0, trialEndsAt.getTime() - now.getTime()) : 0,
      },
      hasAccess: !!hasAccess,
      subscriptionActive: !!subActive,
    };
  });

export const listActivePlans = createServerFn({ method: "GET" }).handler(
  async () => {
    const { supabaseAdmin } = await import(
      "@/integrations/supabase/client.server"
    );
    const { data } = await supabaseAdmin
      .from("subscription_plans" as any)
      .select("code, name, description, monthly_price_zar, features, is_active, sort_order")
      .order("sort_order", { ascending: true });
    return { plans: (data ?? []) as any[] };
  },
);

export const createSubscriptionOrder = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((i: unknown) =>
    z.object({ planCode: z.string().min(2).max(40) }).parse(i),
  )
  .handler(async ({ data, context }) => {
    const { supabaseAdmin } = await import(
      "@/integrations/supabase/client.server"
    );
    const { data: plan } = await supabaseAdmin
      .from("subscription_plans" as any)
      .select("code, name, monthly_price_zar, is_active")
      .eq("code", data.planCode)
      .maybeSingle();
    if (!plan) throw new Error("Plan not found");
    if (!(plan as any).is_active) throw new Error("This plan is not yet available");
    const amount = Number((plan as any).monthly_price_zar);
    if (!Number.isFinite(amount) || amount <= 0)
      throw new Error("Plan pricing not configured — please contact support.");

    const reference = makeReference(context.userId);
    const { error } = await supabaseAdmin
      .from("subscription_orders" as any)
      .insert({
        reference,
        user_id: context.userId,
        plan_code: (plan as any).code,
        billing_period: "monthly",
        amount_zar: amount,
        status: "pending",
      });
    if (error) throw new Error(error.message);
    return { reference, amountZar: amount, planCode: (plan as any).code };
  });

export const listMySubscriptionOrders = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const sb = context.supabase;
    const { data } = await sb
      .from("subscription_orders" as any)
      .select(
        "reference, plan_code, amount_zar, status, paid_at, paid_provider_ref, receipt_sent_at, created_at",
      )
      .eq("user_id", context.userId)
      .order("created_at", { ascending: false })
      .limit(50);
    return { orders: (data ?? []) as any[] };
  });

// -------- Admin --------

export const adminListPlans = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    await assertAdmin({ supabase: context.supabase, userId: context.userId });
    const { supabaseAdmin } = await import(
      "@/integrations/supabase/client.server"
    );
    const { data } = await supabaseAdmin
      .from("subscription_plans" as any)
      .select("*")
      .order("sort_order", { ascending: true });
    return { plans: (data ?? []) as any[] };
  });

export const adminSavePlan = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((i: unknown) =>
    z
      .object({
        code: z.string().min(2).max(40),
        name: z.string().min(2).max(120).optional(),
        description: z.string().max(2000).optional(),
        monthlyPriceZar: z.number().min(0).max(1_000_000).optional(),
        features: z.array(z.string().max(200)).max(40).optional(),
        isActive: z.boolean().optional(),
        sortOrder: z.number().int().min(0).max(10_000).optional(),
      })
      .parse(i),
  )
  .handler(async ({ data, context }) => {
    await assertAdmin({ supabase: context.supabase, userId: context.userId });
    const { supabaseAdmin } = await import(
      "@/integrations/supabase/client.server"
    );
    const patch: Record<string, any> = {};
    if (data.name !== undefined) patch.name = data.name;
    if (data.description !== undefined) patch.description = data.description;
    if (data.monthlyPriceZar !== undefined)
      patch.monthly_price_zar = data.monthlyPriceZar;
    if (data.features !== undefined) patch.features = data.features;
    if (data.isActive !== undefined) patch.is_active = data.isActive;
    if (data.sortOrder !== undefined) patch.sort_order = data.sortOrder;
    const { error } = await supabaseAdmin
      .from("subscription_plans" as any)
      .update(patch)
      .eq("code", data.code);
    if (error) throw new Error(error.message);
    return { ok: true };
  });

export const adminListSubscriptions = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    await assertAdmin({ supabase: context.supabase, userId: context.userId });
    const { supabaseAdmin } = await import(
      "@/integrations/supabase/client.server"
    );
    const { data: subs } = await supabaseAdmin
      .from("user_subscriptions" as any)
      .select(
        "user_id, plan_code, status, current_period_start, current_period_end, next_billing_at, last_order_reference, updated_at",
      )
      .order("updated_at", { ascending: false })
      .limit(200);
    const userIds = Array.from(
      new Set((subs ?? []).map((r: any) => r.user_id)),
    );
    const { data: profs } =
      userIds.length > 0
        ? await supabaseAdmin
            .from("profiles")
            .select("id, email, full_name, trial_ends_at")
            .in("id", userIds)
        : { data: [] as any[] };
    const profMap = new Map((profs ?? []).map((p: any) => [p.id, p]));
    return {
      rows: (subs ?? []).map((s: any) => ({
        ...s,
        email: profMap.get(s.user_id)?.email ?? null,
        full_name: profMap.get(s.user_id)?.full_name ?? null,
        trial_ends_at: profMap.get(s.user_id)?.trial_ends_at ?? null,
      })),
    };
  });
